/* ============================================================
   FGO BUY BOX — v4.25 (16 Sep 2026), every product template
   ------------------------------------------------------------
   Lifted out of pdp-v2.js so the plain product template gets the
   same buy box. Owns: the price in the Add to cart label, the size
   dropdown (open/close, mirrored choice, servings, marks), the
   delivery cards' per-serving lines, the frequency pills (written
   into the hidden .productFormSellingOptionPlan the theme posts),
   and the cadence default by size. Everything the theme's own
   main-product.js reads is left exactly where it was.
   ============================================================ */
(function () {
  'use strict';
  var run = function () {
  (function buyColumn() {
    var section = document.querySelector('.product');
    if (!section) return;

    var jsonEl = section.querySelector('.productJSON');
    if (!jsonEl) return;
    var product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!product || !product.variants) return;

    var money = function (cents) {
      return '$' + (cents / 100).toFixed(2);
    };

    /* ---- 1. price in the ATC button --------------------------
       The theme resets the button with textContent on every variant
       change, which would destroy a child span. A custom property
       plus ::after survives that, because pseudo-elements and CSS
       variables are untouched by textContent. */
    var atc = section.querySelector('.productATC');

    var syncAtcPrice = function () {
      if (!atc) return;
      var priceEl = section.querySelector('.productPriceActual');
      var text = priceEl ? priceEl.textContent.trim() : '';
      if (text && !atc.disabled) {
        atc.style.setProperty('--atc-price', '" · ' + text + '"');
      } else {
        atc.style.setProperty('--atc-price', '""');
      }
    };

    /* ---- 2. per-cup price under each size -------------------
       Count comes from the option value ("100 Ct" -> 100). Liquid's
       integer division would lose the penny, so it is done here in
       float and rounded once. */
    /* v4.25: servings per variant come from Liquid (custom.servings metafield,
       else the bag count from an "N Ct" title), so loose leaf and powders get a
       per-serving line the moment the team fills the metafield. */
    var servMap = null;
    var servingsOf = function (variant) {
      if (servMap === null) {
        servMap = {};
        var host = section.querySelector('[data-servings]');
        if (host) { try { servMap = JSON.parse(host.getAttribute('data-servings')) || {}; } catch (e) { servMap = {}; } }
      }
      var n = parseInt(servMap[String(variant.id)], 10);
      if (n > 0) return n;
      var m = String(variant.title).match(/(\d+)\s*ct\b/i);
      return m ? parseInt(m[1], 10) : 0;
    };
    var perCup = function (variant) {
      var count = servingsOf(variant);
      if (!count) return null;
      return Math.round(variant.price / count);   /* cents per serving */
    };
    /* value basis for the BEST VALUE mark: servings when known, else ounces
       ("16 oz (2-pk)" counts as 32); same rule as the Liquid first paint */
    var unitsOf = function (variant) {
      var n = servingsOf(variant);
      if (n > 0) return n;
      var m = String(variant.title).match(/(\d+(?:\.\d+)?)\s*oz\b/i);
      if (!m) return 0;
      var oz = parseFloat(m[1]);
      if (/2-pk/i.test(String(variant.title))) oz *= 2;
      return oz;
    };
    var perUnit = function (variant) {
      var u = unitsOf(variant);
      return u > 0 ? variant.price / u : null;
    };
    /* the size marks: "Most popular" on the default size, an optional second
       mark on the lowest per-serving size; both worded in the section settings */
    var ddHost = section.querySelector('[data-dd-btn]');
    var flagFirst = ddHost ? (ddHost.getAttribute('data-flag-first') || '') : '';
    var flagValue = ddHost ? (ddHost.getAttribute('data-flag-value') || '') : (product.variants.length > 1 ? 'Best value' : '');

    var bestId = null;   /* v4.25: the dropdown button shows the mark of the chosen size */
    var flagFor = function (variant, best) {
      if (product.variants.length < 2) return '';
      if (flagValue && best && variant.id === best.id) return flagValue;
      if (flagFirst && product.variants[0] && variant.id === product.variants[0].id) return flagFirst;
      return '';
    };
    var annotateSizes = function () {
      var labels = section.querySelectorAll('.productFormOptionsItemValuesLabel');
      if (!labels.length) return;

      /* work out which variant is cheapest per unit, for BEST VALUE; the mark is for the big
         pack, so a smaller size that is cheaper per unit (a pricing anomaly) means no mark */
      var best = null, biggest = null;
      product.variants.forEach(function (v) {
        var p = perUnit(v), u = unitsOf(v);
        if (p !== null && (best === null || p < best.p)) best = { p: p, id: v.id };
        if (u > 0 && (biggest === null || u > biggest.u)) biggest = { u: u, id: v.id };
      });
      if (best && biggest && best.id !== biggest.id) best = null;
      bestId = best ? best.id : null;

      labels.forEach(function (label) {
        if (label.dataset.annotated) return;
        var input = label.querySelector('input');
        if (!input) return;
        var value = input.value;
        var variant = product.variants.filter(function (v) {
          return v.options.indexOf(value) > -1;
        })[0];
        if (!variant) return;

        var p = perCup(variant);
        var selectorEl = label.querySelector('.productFormOptionsItemValuesLabelSelector');
        if (p !== null) {
          var note = document.createElement('span');
          note.className = 'pdpPerCup';
          note.textContent = money(p) + ' per serving';
          label.appendChild(note);

          /* v4.25: "40 servings" beside the name in the dropdown rows */
          var meta = document.createElement('span');
          meta.className = 'pdpDdRowMeta';
          meta.textContent = servingsOf(variant) + ' servings';
          if (selectorEl) selectorEl.appendChild(meta);
        }

        var flagText = flagFor(variant, best);
        if (flagText) {
          var flag = document.createElement('span');
          flag.className = 'pdpBestValue';
          flag.textContent = flagText;
          (selectorEl || label).appendChild(flag);
        }
        label.dataset.annotated = '1';
      });
    };

    /* ---- 3. mirror the selected size beside the label -------- */
    var mirrorSelection = function () {
      var group = section.querySelector('.productFormOptionsItem:not(.mobileOnly)');
      if (!group) return;
      var labelEl = group.querySelector('.productFormOptionsItemLabel');
      var checked = group.querySelector('input:checked');
      if (!labelEl || !checked) return;

      var pick = labelEl.querySelector('.pdpPick');
      if (!pick) {
        pick = document.createElement('span');
        pick.className = 'pdpPick';
        labelEl.appendChild(pick);
      }
      pick.textContent = checked.value;
    };

    /* ---- 4. mirror the purchase choice beside its label ------ */
    var mirrorPurchase = function () {
      var wrap = section.querySelector('.pdpDeliv') || section.querySelector('.pdpPurchase');
      if (!wrap) return;
      var pick = wrap.querySelector('[data-purchase-pick]');
      /* the frequency pills are radios too, so ask for the purchase pair by name */
      var chosen = wrap.querySelector('input[name$="-purchase"]:checked') || wrap.querySelector('input:checked');
      if (!pick || !chosen) return;
      var pct = wrap.getAttribute('data-sub-pct');
      if (wrap.classList.contains('pdpDeliv')) {
        pick.textContent = chosen.value === 'sub' ? ('Subscribe & Save' + (pct ? ' ' + pct + '%' : '')) : 'Try Once';
      } else {
        pick.textContent = chosen.value === 'sub' ? 'Subscribe & save' : 'One-time';
      }
    };

    /* ============================================================
       v4.25 BUY BOX (16 Sep 2026)
       · the pack-size dropdown: opens the option list, mirrors the
         checked size (name, "N cups", Best value) into the button
       · the frequency pills: write the chosen plan id into the hidden
         .productFormSellingOptionPlan the theme posts to the cart, and
         picking a pill selects Subscribe
       · the per-cup line under each card price
       · cadence by size: a 100-count box opens on every 2 months when
         the size changes (Liquid sets the first paint the same way)
       ============================================================ */
    var ddBtn = section.querySelector('[data-dd-btn]');
    var ddItem = ddBtn ? ddBtn.closest('.productFormOptionsItem') : null;
    var setDdOpen = function (on) {
      if (!ddItem) return;
      ddItem.classList.toggle('is-open', on);
      ddBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
    };
    var sizeDropdown = function () {
      if (!ddBtn || !ddItem) return;
      ddBtn.addEventListener('click', function () { setDdOpen(!ddItem.classList.contains('is-open')); });
      document.addEventListener('click', function (e) { if (!ddItem.contains(e.target)) setDdOpen(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && ddItem.classList.contains('is-open')) { setDdOpen(false); ddBtn.focus(); } });
      ddItem.addEventListener('change', function (e) {
        if (e.target && e.target.classList.contains('productFormOptionsItemValuesLabelInput')) { setDdOpen(false); ddBtn.focus(); }
      });
    };
    var syncDd = function () {
      if (!ddBtn || !ddItem) return;
      var checked = ddItem.querySelector('input:checked');
      if (!checked) return;
      var name = ddBtn.querySelector('[data-dd-name]');
      if (name) name.textContent = checked.value;
      var variant = product.variants.filter(function (v) { return v.options.indexOf(checked.value) > -1; })[0];
      var meta = ddBtn.querySelector('[data-dd-meta]');
      var n = variant ? servingsOf(variant) : 0;
      if (meta) {
        meta.textContent = n ? n + ' servings' : '';
        meta.hidden = !n;
      }
      var flag = ddBtn.querySelector('[data-dd-flag]');
      if (flag) {
        var text = variant ? flagFor(variant, bestId ? { id: bestId } : null) : '';
        flag.textContent = text;
        flag.hidden = !text;
      }
    };

    var activeVariant = function () {
      var groups = section.querySelectorAll('.productFormOptionsItem:not(.mobileOnly)');
      if (!groups.length) return product.variants[0] || null;
      var options = [];
      for (var i = 0; i < groups.length; i++) {
        var c = groups[i].querySelector('input:checked');
        if (!c) return null;
        options.push(c.value);
      }
      return product.variants.filter(function (v) { return JSON.stringify(v.options) === JSON.stringify(options); })[0] || null;
    };
    var planInput = section.querySelector('.pdpDeliv .productFormSellingOptionPlan');
    var pills = function () { return section.querySelectorAll('.pdpDelivCads input[type="radio"]'); };
    var syncPlanInput = function () {
      var on = section.querySelector('.pdpDelivCads input:checked');
      if (on && planInput) planInput.value = on.value;
    };
    var defaultCadence = function (v) {
      var ps = pills();
      if (!ps.length) return;
      var wantTwo = /\b100\b/.test(String(v.title));
      var target = null;
      if (wantTwo) {
        for (var i = 0; i < ps.length; i++) {
          var words = (ps[i].parentNode.textContent || '').toLowerCase();
          if (/2 month/.test(words)) { target = ps[i]; break; }
        }
      }
      if (!target) target = ps[0];
      target.checked = true;
      syncPlanInput();
    };
    var perCupCards = function (v) {
      var onceEl = section.querySelector('[data-once-percup]');
      var subEl = section.querySelector('[data-sub-percup]');
      var n = servingsOf(v);
      if (onceEl) onceEl.textContent = n ? money(Math.round(v.price / n)) + '/serving' : '';
      /* price drop: the compare-at price struck beside Try Once when it is higher than the price */
      var cmp = section.querySelector('[data-once-compare]');
      if (cmp) { var was = v.compare_at_price; if (was && was > v.price) { cmp.textContent = money(was); cmp.hidden = false; } else { cmp.hidden = true; } }
      var alloc = v.selling_plan_allocations && v.selling_plan_allocations[0];
      if (subEl) subEl.textContent = (n && alloc) ? money(Math.round(alloc.price / n)) + '/serving' : '';
    };
    var lastVariantId = null;
    var syncDeliv = function () {
      var v = activeVariant();
      if (!v) return;
      if (lastVariantId !== null && lastVariantId !== v.id) defaultCadence(v);
      lastVariantId = v.id;
      perCupCards(v);
      syncPlanInput();
    };
    if (section.querySelector('.pdpDeliv')) {
      sizeDropdown();
      /* the card is the control: whatever the theme does to the radio, a click
         anywhere on a card (outside the pills) selects it */
      section.addEventListener('click', function (e) {
        var card = e.target && e.target.closest ? e.target.closest('.pdpDelivCard') : null;
        if (!card) return;   /* the pills included: a click on an already-chosen pill fires no change, so the card must still wake up */
        var input = card.querySelector('.pdpDelivInput');
        if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      section.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.type !== 'radio' || !t.closest('.pdpDelivCads')) return;
        syncPlanInput();
        var sub = section.querySelector('.productFormSellingOptionHeaderInput[value="sub"]');
        if (sub && !sub.checked) {
          sub.checked = true;
          sub.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    var syncAll = function () {
      annotateSizes();
      mirrorSelection();
      mirrorPurchase();
      syncAtcPrice();
      syncDd();
      syncDeliv();
    };

    syncAll();

    /* the theme rewrites price and button on change; run after it */
    section.addEventListener('change', function () { setTimeout(syncAll, 0); });
    /* and once more after its own load-time update settles */
    setTimeout(syncAll, 350);
  })();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
